import { useQuery } from '@apollo/client'
import {
  CheckIcon,
  CloseIcon,
} from '@danone-global/internal/react/components/icons'
import LineItem from '@danone-global/internal/react/components/line-item'
import RelatedProducts from '@danone-global/internal/react/components/related-products'
import {
  Button,
  Grid,
  Hr,
  IconButton,
  Typography,
} from '@danone-global/internal/react/components/ui'
import {
  GET_QUICK_VIEW_STATE,
  isValue,
  SET_QUICK_VIEW_STATE,
  Theme,
  useCart,
  useCore,
  useMutation,
} from '@danone-global/internal/react/core'
import { makeStyles } from '@material-ui/styles'
import React, { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { CSSTransition } from 'react-transition-group'

export interface Props {
  goToCartPage: () => void
  withAnalytics?: boolean
}

export const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      position: 'fixed',
      top: theme.spacing(2),
      right: theme.spacing(2),
      minWidth: theme.utils.getSize(450),
      maxWidth: theme.utils.getSize(450),
      boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.07)',
      borderRadius: theme.shape.borderRadius(),
      padding: theme.spacing(3),
      backgroundColor: theme.palette.background.default,
      zIndex: 1000,

      [theme.breakpoints.down('sm')]: {
        minWidth: `calc(100% - ${theme.spacing(4)})`,
        width: `calc(100% - ${theme.spacing(4)})`,
      },
    },

    addedIcon: {
      backgroundColor: theme.palette.secondary.main,
      borderRadius: '50%',
      padding: theme.utils.getSize(6),
      marginRight: theme.spacing(),
      fill: theme.palette.common.white,
    },

    title: {},

    enter: {
      opacity: 0,
    },

    exit: {
      opacity: 1,
    },

    exitActive: {
      opacity: 0,
      transition: 'opacity 300ms',
    },

    enterActive: {
      opacity: 1,
      transition: 'opacity 300ms',
    },

    hr: {
      margin: theme.spacing(2, -3),
      width: theme.utils.createStyle('calc(100% +', theme.spacing(6), ')'),
    },

    closeIcon: {
      padding: 0,
    },

    buttonsContainer: {
      marginTop: theme.spacing(4),

      '& button:first-of-type': {
        marginBottom: theme.spacing(),
      },
    },

    button: {
      justifyContent: 'center',
    },
  }),
  { name: 'QuickView' },
)

export const QuickView: React.FC<Props> = ({
  goToCartPage,
  withAnalytics = true,
}) => {
  const { features } = useCore()
  const { data } = useQuery<{
    quickViewState: {
      open: boolean
      skus: string[]
      giftSkus?: string[]
      packageComposition?: string[]
    }
  }>(GET_QUICK_VIEW_STATE)
  const [updateQuickView] = useMutation(SET_QUICK_VIEW_STATE)
  const [cart] = useCart()
  const timeout = React.useRef(null)
  const openedSkus = React.useRef([])
  const previousOpened = React.useRef(false)
  const previousLineItems = React.useRef(null)
  const classes = useStyles()

  const removeFadeOut = React.useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current)
    }
  }, [])

  const hideQuickView = React.useCallback(async () => {
    removeFadeOut()

    await updateQuickView({
      variables: {
        open: false,
        skus: [],
        giftSkus: [],
      },
    })
  }, [])

  const fadeOut = React.useCallback(() => {
    removeFadeOut()

    timeout.current = setTimeout(hideQuickView, 4000)
  }, [])

  useEffect(() => {
    if (data?.quickViewState?.open && !previousOpened.current) {
      fadeOut()
    }

    previousOpened.current = data?.quickViewState?.open
  }, [data?.quickViewState, fadeOut])

  useEffect(() => {
    return () => {
      hideQuickView()
    }
  }, [])

  // Get the line items that were added
  const lineItems = React.useMemo(() => {
    openedSkus.current = data?.quickViewState?.skus || openedSkus.current
    if (openedSkus.current.length === 0) {
      return []
    }

    const newLineItems = cart?.lineItems.filter((lineItem) => {
      if (data?.quickViewState.packageComposition) {
        // When available, compare packageComposition to get the correct lineItem
        const lineItemPackageComposition =
          lineItem.custom?.customFieldsRaw.find(
            (i) => i.name === 'packageComposition',
          )?.value

        return (
          openedSkus.current.includes(lineItem.variant.sku) &&
          JSON.stringify(data?.quickViewState.packageComposition) ===
            JSON.stringify(lineItemPackageComposition)
        )
      } else {
        return openedSkus.current.includes(lineItem.variant.sku)
      }
    })

    if (!newLineItems) {
      hideQuickView()

      // This makes sure the quick view fades out nice instead
      // of having the product disappear first
      return previousLineItems.current
    }

    return (previousLineItems.current = newLineItems)
  }, [hideQuickView, data?.quickViewState, cart?.lineItems])

  const giftLineItems = data?.quickViewState?.giftSkus
    ?.map((sku) => cart.lineItems?.find((l) => l.variant.sku === sku))
    .filter(isValue)

  return (
    <CSSTransition
      key="quick-view"
      classNames={{
        enter: classes.enter,
        enterActive: classes.enterActive,
        exit: classes.exit,
        exitActive: classes.exitActive,
      }}
      in={data?.quickViewState?.open}
      timeout={400}
      unmountOnExit
    >
      <div
        className={classes.root}
        data-testid="quick-view"
        onMouseEnter={removeFadeOut}
        onMouseLeave={fadeOut}
      >
        <Grid alignItems="center" direction="row" container>
          <Grid alignItems="center" flex item>
            <CheckIcon className={classes.addedIcon} />

            <Typography
              className={classes.title}
              color="secondary"
              fontWeight="bold"
              variant="body"
              noGutters
            >
              <FormattedMessage
                defaultMessage="Product added to cart"
                id="quick-view.header.product-added"
              />
            </Typography>
          </Grid>

          <IconButton
            className={classes.closeIcon}
            color="primary-main"
            data-testid="btn.quick-view.close"
            onClick={hideQuickView}
            variant="flat"
          >
            <CloseIcon height={20} width={20} />
          </IconButton>
        </Grid>

        <Hr className={classes.hr} />

        {lineItems?.length
          ? lineItems.map((lineItem) => (
              <LineItem
                key={lineItem.id}
                lineItem={lineItem}
                variant="quick-view"
                withAnalytics={withAnalytics}
              />
            ))
          : undefined}

        {giftLineItems?.length && features.showGiftLineItems
          ? giftLineItems.map((giftLineItem) => (
              <div key={giftLineItem.id}>
                <Hr className={classes.hr} />
                <LineItem
                  lineItem={giftLineItem}
                  variant="quick-view"
                  withAnalytics={withAnalytics}
                />
              </div>
            ))
          : undefined}

        <Hr className={classes.hr} />

        {features.showRelatedProducts && (
          <RelatedProducts
            allowedCartSkus={openedSkus.current}
            variant="quick-view"
            withAnalytics={withAnalytics}
          />
        )}

        <Grid className={classes.buttonsContainer} direction="column" flex>
          <Button
            className={classes.button}
            data-testid="btn.quick-view.view-cart"
            onClick={goToCartPage}
            variant="brand"
          >
            <FormattedMessage
              defaultMessage="View Cart"
              id="quick-view.header.view-cart"
            />
          </Button>

          <Button
            className={classes.button}
            data-testid="btn.quick-view.continue-shopping"
            onClick={hideQuickView}
          >
            <FormattedMessage
              defaultMessage="Continue shopping"
              id="quick-view.header.continue-shopping"
            />
          </Button>
        </Grid>
      </div>
    </CSSTransition>
  )
}

export default QuickView
